import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { FiLink, FiTrendingUp, FiEye, FiExternalLink, FiCopy, FiCheck, FiArrowLeft } from 'react-icons/fi';
import Shuffle from './Shuffle';
import axios from 'axios';
import './Home.css';

// Use relative path for production, or env variable if set
const API_URL = process.env.REACT_APP_API_URL || '';

function MyLinks() {
  const { wallet } = useParams();
  const navigate = useNavigate();
  const { connected, publicKey } = useWallet();
  const [myLinks, setMyLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedUuid, setCopiedUuid] = useState(null);
  const [features, setFeatures] = useState({ my_ca_enabled: true });

  useEffect(() => {
    loadFeatures();
  }, []);

  useEffect(() => {
    if (wallet && features.my_ca_enabled) {
      loadMyLinks();
    }
  }, [wallet, features]);

  const loadFeatures = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/features`);
      setFeatures(response.data);
      if (!response.data.my_ca_enabled) {
        navigate('/');
      }
    } catch (err) {
      console.error('Failed to load features:', err);
    }
  };

  const loadMyLinks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/my-links/${wallet}`);
      setMyLinks(response.data || []);
    } catch (err) {
      console.error('Failed to load my links:', err);
      setMyLinks([]);
    } finally {
      setLoading(false);
    }
  };

  const copyItemUrl = (uuid) => {
    const url = `${window.location.origin}/ca/${uuid}`;
    navigator.clipboard.writeText(url);
    setCopiedUuid(uuid);
    setTimeout(() => setCopiedUuid(null), 2000);
  };

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toFixed(2);
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return 'N/A';
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  const getPercentColor = (num) => {
    if (num === null || num === undefined) return '#888';
    return num >= 0 ? '#10b981' : '#ef4444';
  };

  return (
    <div className="home-container">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <Shuffle
              text="Copy CA"
              shuffleDirection="right"
              duration={0.35}
              animationMode="evenodd"
              shuffleTimes={1}
              ease="power3.out"
              stagger={0.03}
              threshold={0.1}
              triggerOnce={true}
              triggerOnHover
              respectReducedMotion={true}
              loop={true}
              loopDelay={3}
              tag="h1"
              className="logo-shuffle"
            />
          </div>
          <div className="header-nav">
            <a href="/" className="my-links-link">
              <FiArrowLeft style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Home
            </a>
            {connected && publicKey && (
              <a href={`/my-links/${publicKey.toString()}`} className="my-links-link">
                My Links
              </a>
            )}
            <div className="wallet-header">
              <WalletMultiButton />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* My Links Section */}
        <section className="top-section">
          <div className="top-card" style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 className="section-title">My Links</h2>
            {loading ? (
              <div className="loading">Loading...</div>
            ) : !myLinks || myLinks.length === 0 ? (
              <div className="empty-state">No links created yet</div>
            ) : (
              <div className="top-list">
                {myLinks.map((item, index) => (
                  <div key={item.uuid} className="top-item">
                    <div className="top-item-rank">#{index + 1}</div>
                    <div className="top-item-content">
                      <div className="top-item-header">
                        <div className="top-item-title-group">
                          {item.logo_url && (
                            <div className="top-item-logo">
                              <img
                                src={item.logo_url}
                                alt={item.name || item.symbol || 'Token logo'}
                              />
                            </div>
                          )}
                          <h3 className="top-item-name">
                            {item.name || item.symbol || 'Unknown Token'}
                            {item.symbol && <span className="top-item-symbol">({item.symbol})</span>}
                          </h3>
                        </div>
                      </div>
                      <div className="top-item-details">
                        <div className="top-item-detail">
                          <span className="detail-label">Views:</span>
                          <span className="detail-value">{item.views}</span>
                        </div>
                        {item.market_cap > 0 && (
                          <div className="top-item-detail">
                            <span className="detail-label">Market Cap:</span>
                            <span className="detail-value">{formatNumber(item.market_cap)}</span>
                          </div>
                        )}
                        {item.price_change_24h !== undefined && (
                          <div className="top-item-detail">
                            <span className="detail-label">24h Change:</span>
                            <span 
                              className="detail-value"
                              style={{ color: getPercentColor(item.price_change_24h) }}
                            >
                              {formatPercent(item.price_change_24h)}
                            </span>
                          </div>
                        )}
                      </div>
                      {(item.website_url || item.twitter_url) && (
                        <div className="top-item-links">
                          {item.website_url && (
                            <a
                              href={item.website_url}
                              className="top-item-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {item.website_label || 'Website'}
                            </a>
                          )}
                          {item.twitter_url && (
                            <a
                              href={item.twitter_url}
                              className="top-item-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Twitter
                            </a>
                          )}
                        </div>
                      )}
                      <div className="top-item-ca">
                        <code>{item.CA}</code>
                      </div>
                      <div className="top-item-actions">
                        <a 
                          href={`/ca/${item.uuid}`} 
                          className="open-button"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FiExternalLink /> Open
                        </a>
                        <button
                          type="button"
                          className={`open-button copy-url-button ${copiedUuid === item.uuid ? 'copied' : ''}`}
                          onClick={() => copyItemUrl(item.uuid)}
                        >
                          {copiedUuid === item.uuid ? (
                            <>
                              <FiCheck /> Copied!
                            </>
                          ) : (
                            <>
                              <FiCopy /> Copy URL
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default MyLinks;
